import React, { useState } from 'react';
import Papa from 'papaparse';
import api from '../services/api';

function ImportData() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [importType, setImportType] = useState('books');
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setSelectedFile(file);
      setError('');
      previewFile(file);
    } else {
      setError('Please select a valid CSV file');
      setSelectedFile(null);
      setPreview(null);
    }
  };

  const previewFile = (file) => {
    Papa.parse(file, {
      header: true,
      preview: 5,
      complete: (results) => {
        setPreview(results.data);
      },
      error: (error) => {
        setError('Error reading CSV file: ' + error.message);
      }
    });
  };

  const validateHeaders = (headers, type) => {
    const requiredHeaders = {
      books: ['title', 'author'],
      users: ['first_name', 'last_name', 'teacher']
    };

    const required = requiredHeaders[type];
    const missing = required.filter(header => !headers.includes(header));
    
    return missing.length === 0 ? null : `Missing required columns: ${missing.join(', ')}`;
  };

  const importData = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setImporting(true);
    setError('');
    setResults(null);

    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const headers = Object.keys(results.data[0] || {});
          const headerError = validateHeaders(headers, importType);
          
          if (headerError) {
            setError(headerError);
            setImporting(false);
            return;
          }

          const response = await api.post(`/import/${importType}`, {
            data: results.data
          });

          setResults(response.data);
        } catch (error) {
          setError(error.response?.data?.message || 'Import failed');
        } finally {
          setImporting(false);
        }
      },
      error: (error) => {
        setError('Error parsing CSV: ' + error.message);
        setImporting(false);
      }
    });
  };

  const downloadTemplate = () => {
    const templates = {
      books: 'title,author\n"Sample Book Title","Sample Author"\n"Another Book","Another Author"',
      users: 'first_name,last_name,teacher\n"John","Doe","Mrs. Smith"\n"Jane","Smith","Mr. Johnson"'
    };

    const blob = new Blob([templates[importType]], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${importType}_template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div>
      <h2>Import Data from CSV</h2>

      {error && (
        <div style={{
          color: '#dc3545',
          backgroundColor: '#f8d7da',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      <div style={{marginBottom: '20px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '4px'}}>
        <h3>Import Settings</h3>
        
        <div style={{marginBottom: '15px'}}>
          <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>
            Import Type:
          </label>
          <select
            value={importType}
            onChange={(e) => setImportType(e.target.value)}
            style={{padding: '8px', border: '1px solid #ddd', borderRadius: '4px', width: '200px'}}
          >
            <option value="books">Books</option>
            <option value="users">Users</option>
          </select>
        </div>

        <div style={{marginBottom: '15px'}}>
          <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>
            CSV File:
          </label>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
          />
        </div>

        <div style={{display: 'flex', gap: '10px', marginBottom: '15px'}}>
          <button
            onClick={downloadTemplate}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Download Template
          </button>
          <button
            onClick={importData}
            disabled={!selectedFile || importing}
            style={{
              padding: '8px 16px',
              backgroundColor: importing ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: importing || !selectedFile ? 'not-allowed' : 'pointer'
            }}
          >
            {importing ? 'Importing...' : 'Import Data'}
          </button>
        </div>

        <div style={{fontSize: '14px', color: '#6c757d'}}>
          <strong>Required columns for {importType}:</strong>
          <ul style={{margin: '5px 0', paddingLeft: '20px'}}>
            {importType === 'books' && (
              <>
                <li>title - The book title</li>
                <li>author - The book author</li>
              </>
            )}
            {importType === 'users' && (
              <>
                <li>first_name - Student's first name</li>
                <li>last_name - Student's last name</li>
                <li>teacher - Student's teacher</li>
              </>
            )}
          </ul>
        </div>
      </div>

      {preview && (
        <div style={{marginBottom: '20px'}}>
          <h3>File Preview (first 5 rows)</h3>
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', backgroundColor: 'white'}}>
              <thead>
                <tr style={{backgroundColor: '#f8f9fa'}}>
                  {Object.keys(preview[0] || {}).map((header) => (
                    <th key={header} style={{padding: '8px', border: '1px solid #ddd', textAlign: 'left'}}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((row, index) => (
                  <tr key={index}>
                    {Object.values(row).map((value, cellIndex) => (
                      <td key={cellIndex} style={{padding: '8px', border: '1px solid #ddd'}}>
                        {value}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {results && (
        <div style={{marginTop: '20px', padding: '20px', backgroundColor: '#d4edda', borderRadius: '4px', border: '1px solid #c3e6cb'}}>
          <h3>Import Results</h3>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px'}}>
            <div>
              <strong>Total Processed:</strong> {results.total}
            </div>
            <div>
              <strong>Successfully Imported:</strong> {results.successful}
            </div>
            <div>
              <strong>Failed:</strong> {results.failed}
            </div>
            <div>
              <strong>Duplicates Skipped:</strong> {results.duplicates || 0}
            </div>
          </div>
          
          {results.errors && results.errors.length > 0 && (
            <div style={{marginTop: '15px'}}>
              <h4>Errors:</h4>
              <ul style={{margin: 0, paddingLeft: '20px'}}>
                {results.errors.slice(0, 10).map((error, index) => (
                  <li key={index} style={{color: '#721c24'}}>{error}</li>
                ))}
                {results.errors.length > 10 && (
                  <li style={{color: '#721c24'}}>... and {results.errors.length - 10} more errors</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ImportData;
